/**
 * Утилиты для работы с ценами
 * Обрабатывает объекты Money {amount: 25000, currency: 'RUB'} и преобразует в числа
 */

/**
 * Безопасное преобразование цены из объекта Money или числа/строки в число (в рублях)
 * @param priceValue - Цена в виде объекта Money, числа или строки
 * @returns Цена в рублях (number)
 */
export function parsePrice(priceValue: unknown): number {
  // ПРИОРИТЕТ: Если объект Money {amount: 25000, currency: 'RUB'} - проверяем первым
  if (priceValue && typeof priceValue === 'object' && !Array.isArray(priceValue)) {
    const priceRecord = priceValue as Record<string, unknown>
    const amount =
      (priceRecord['amount'] as number | string | undefined) ??
      (priceRecord['value'] as number | string | undefined) ??
      (priceRecord['price'] as number | string | undefined) ??
      (priceRecord['amountInMinorUnits'] as number | string | undefined) ??
      0

    // amount в копейках, делим на 100 для получения рублей
    if (typeof amount === 'number' && !isNaN(amount)) {
      return amount / 100
    }
    if (typeof amount === 'string') {
      const parsed = parseFloat(amount)
      return isNaN(parsed) ? 0 : parsed / 100
    }
    return 0
  }

  // Если уже число и не NaN - считаем, что уже в рублях
  if (typeof priceValue === 'number' && !isNaN(priceValue)) {
    return priceValue
  }

  // Если строка
  if (typeof priceValue === 'string') {
    const trimmed = priceValue.trim()
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
      return 0
    }
    const parsed = parseFloat(trimmed)
    return isNaN(parsed) ? 0 : parsed
  }

  // Если null или undefined
  if (priceValue === null || priceValue === undefined) {
    return 0
  }

  return 0
}

/**
 * Форматирование цены для отображения
 * @param price - Цена в рублях (number) или объект Money
 * @returns Отформатированная строка (например, "25 000 ₽")
 */
export function formatPrice(price: unknown): string {
  const priceInRubles = parsePrice(price)

  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(priceInRubles)
}

/**
 * Форматирование цены с копейками
 * @param price - Цена в рублях (number) или объект Money
 * @returns Отформатированная строка с копейками (например, "25 000,50 ₽")
 */
export function formatPriceWithCents(price: unknown): string {
  const priceInRubles = parsePrice(price)

  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceInRubles)
}

/**
 * Форматирование цены для админки (аналог formatCurrency)
 * @param price - Цена в рублях (number) или объект Money
 * @returns Отформатированная строка
 */
export function formatCurrency(price: unknown): string {
  return formatPrice(price)
}
